import { Component, h, RefObject, createRef } from 'preact';
import { Subject, Subscription } from 'rxjs';
import cx from 'classnames';

import { ChoicesButtonGroup } from '../../common/choices-button-group.component';
import { NATIONALITIES } from '../../../../../constants/nationalities';
import { InquiryDocument } from 'inquiries/inquiry-document.model';

import { Form, FieldList, Field } from '../../../../utils/forms/form';
import * as Validators from '../../../../utils/forms/validators';
import { cxValidateField } from '../../../../utils/forms/validators';
import { handlers } from '../../../../core/multi-event-handler';
import hopplerLinkState, { hopplerUpdateFormFieldValue } from '../../../../utils/forms/hoppler-linkstate';

import { DocumentValidators } from './document-validators';
import { InquiryDocumentType } from '../../../../../constants/inquiries';
import { InqDocumentModalTabs, DocumentTabID } from './inq-document-modal-tabs.component';

interface InqDocumentNamesFormProps {
  document: InquiryDocument;
  documentType: InquiryDocumentType;

  activeTab: DocumentTabID;
  onTabClick: (tabId: DocumentTabID) => void;
  
  onDocumentChange: (document: InquiryDocument) => void;
  onNextClick: () => void;
}

interface InqDocumentNamesFormState {
  form?: Form;
}

export class InqDocumentNamesForm extends Component<InqDocumentNamesFormProps, InqDocumentNamesFormState> {
  form$ = new Subject<Form>();
  subscriptions: Subscription[] = [];
  refs: { [k: string]: RefObject<HTMLElement> } = {};
  formScrollerRef = createRef<HTMLDivElement>();

  __linkToDocumentCallbackCache: { [k: string]: (event: Event) => void } = {};

  componentWillMount() {
    this.setupForm();
  }

  createFormFields(doc: InquiryDocument, documentType: InquiryDocumentType) {
    const brokerValidator = DocumentValidators.requiredIf(this.props.documentType === InquiryDocumentType.OFFER);
    const fields: FieldList = {
      'clientSalutation': new Field([ Validators.required ], null, doc.clientSalutation),
      'clientFirstName': new Field([ Validators.required ], null, doc.clientFirstName),
      'clientLastName': new Field([ Validators.required ], null, doc.clientLastName),
      'clientOfficeAddress': new Field([], null, doc.clientOfficeAddress),
      'clientEmployer': new Field([], null, doc.clientEmployer),
      'clientEthnicity': new Field([ Validators.required ], null, doc.clientEthnicity),
      'ownerSalutation': new Field([ Validators.required ], null, doc.ownerSalutation),
      'ownerFirstName': new Field([ Validators.required ], null, doc.ownerFirstName),
      'ownerLastName': new Field([ Validators.required ], null, doc.ownerLastName),
      'brokerName': new Field([], [ brokerValidator ], doc.brokerName),
      'faxNumber': new Field([], null, doc.faxNumber),
    };

    if (documentType === InquiryDocumentType.OFFER) {
      // "Validators.email" rejects empty values, so we must only include this
      // in offer mode and not in contract mode.
      fields['brokerEmail'] = new Field([ Validators.email ], [ brokerValidator ], doc.brokerEmail);
    }

    return fields;
  }

  setupForm() {
    const doc = this.props.document;
    const documentType = this.props.documentType;

    const fields = this.createFormFields(doc, documentType);
    const form = new Form(fields, { pristineUntilEdited: true });
    Validators.validateForm(form);

    this.state = { form };
    this.subscriptions.push( Form.bind(this, this.form$) );

    for (let key in fields) {
      this.refs[key] = createRef();
    }
  }

  linkToDocument(field: string) {
    let callback: (event: Event) => void;
    if (this.__linkToDocumentCallbackCache[field]) {
      // console.log('Reusing linkToDocument callback for', field);
      callback = this.__linkToDocumentCallbackCache[field];
    } else {
      // console.log('Created linkToDocument callback for', field);
      callback = this.__linkToDocumentCallbackCache[field] = event => {
        const target = event.target as HTMLInputElement;
        const value = target.value;

        const newDoc = { ...this.props.document, [field]: value };
        this.props.onDocumentChange(newDoc);
      }
    }

    return callback;
  }

  onValidationScroll(offsetTop: number) {
    // console.log('offsetTop:', offsetTop);
    const scroller = this.formScrollerRef.current;
    const scrollerRect = scroller.getBoundingClientRect();
    // 64px extra vertical space to allow for input labels and spacing
    const targetScroll = offsetTop - scrollerRect.top - 64;

    scroller.scrollTo({ top: targetScroll, left: 0, behavior: 'smooth' });
  }

  onDirectValueChange(value: any, field: string) {
    const newDoc = { ...this.props.document, [field]: value };
    this.props.onDocumentChange(newDoc);

    hopplerUpdateFormFieldValue(this.form$, this.state.form, field, value);
  }

  scrollToFirstError() {
    const form = this.state.form;
    const firstInvalidKey = form.getFirstInvalidFieldName();

    if (firstInvalidKey) {
      const ref = this.refs[firstInvalidKey];
      let current = ref.current;
      // console.log('First invalid, current:', firstInvalidKey, current);

      // If the element is a Preact component, we need to reach inside and get
      // the actual DOM element
      if (current['base']) {
        current = current['base'];
      }

      this.onValidationScroll(current.offsetTop);
    }
  }

  onNextClick() {
    Validators.touchEntireForm(this.form$, this.state.form);
    const form = this.state.form;
    if (!form.isValid) {
      this.scrollToFirstError();
    } else if (this.props.onNextClick) {
      this.props.onNextClick();
    }
  }

  onTabClick(tabId: DocumentTabID) {
    Validators.touchEntireForm(this.form$, this.state.form);
    const form = this.state.form;
    if (!form.isValid) {
      this.scrollToFirstError();
    } else {
      this.props.onTabClick(tabId)
    }
  }

  renderForm(props: InqDocumentNamesFormProps, state: InqDocumentNamesFormState) {
    const document = props.document || {};
    const form$ = this.form$;
    const form = state.form;

    // const start = performance.now();

    const ui = (
      <div className="hi-docnamesform">
        <div className="hi-docnamesform__form-row hi-docnamesform__client-name-group">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Client's Title</label>
            <ChoicesButtonGroup choices={ ['Mr.', 'Ms.', 'Mrs.'] }
              selectedChoice={ document.clientSalutation }
              onSelect={ choice => this.onDirectValueChange(choice, 'clientSalutation') }
              valid={ !cxValidateField(form.fields.clientSalutation) }
              ref={ this.refs.clientSalutation } />
          </div>

          <div className="hi-input-group">
            <label className="hi-input-group__label">Client's Full Name</label>
            <div className="d-flex">
              <input type="text" placeholder="First name" value={ document.clientFirstName }
                className={ cx('form-control w-50 hi-border-radius-right-0', {
                  'is-invalid': cxValidateField(form.fields.clientFirstName)
                }) }
                onChange={ handlers(this.linkToDocument('clientFirstName'), hopplerLinkState(form$, form, 'clientFirstName')) }
                ref={ this.refs.clientFirstName } />
              <input type="text" placeholder="Last name" value={ document.clientLastName }
                className={ cx('form-control w-50 hi-border-radius-left-0', {
                  'is-invalid': cxValidateField(form.fields.clientLastName)
                }) }
                onChange={ handlers(this.linkToDocument('clientLastName'), hopplerLinkState(form$, form, 'clientLastName')) }
                ref={ this.refs.clientLastName } />
            </div>
          </div>
        </div>

        <div className="hi-input-group">
          <label className="hi-input-group__label">Client's Office Address</label>
          <input type="text" placeholder="Office address"
            value={ document.clientOfficeAddress }
            className={ cx('form-control', {
              'is-invalid': cxValidateField(form.fields.clientOfficeAddress)
            }) }
            onChange={ handlers(
              this.linkToDocument('clientOfficeAddress'),
              hopplerLinkState(form$, form, 'clientOfficeAddress')
            ) }
            ref={ this.refs.clientOfficeAddress } />
        </div>

        <div className="hi-docnamesform__form-row hi-docnamesform__client-employer-group">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Client's Employer</label>
            <input type="text" placeholder="Employer" 
              value={ document.clientEmployer }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.clientEmployer)
              }) }
              onChange={ handlers(
                this.linkToDocument('clientEmployer'),
                hopplerLinkState(form$, form, 'clientEmployer')
              ) }
              ref={ this.refs.clientEmployer } />
          </div>

          <div className="hi-input-group">
            <label className="hi-input-group__label">Client's Nationality</label>
            <select value={ document.clientEthnicity }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.clientEthnicity)
              }) }
              onChange={ handlers(
                this.linkToDocument('clientEthnicity'),
                hopplerLinkState(form$, form, 'clientEthnicity')
              ) }
              ref={ this.refs.clientEthnicity }>
              <option disabled>Select</option>
              { NATIONALITIES.map(nationality => <option value={ nationality }>{ nationality }</option>) }
            </select>
          </div>
        </div>


        <div className="hi-docnamesform__form-row hi-docnamesform__owner-title-group">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Owner's Title</label>
            <ChoicesButtonGroup choices={ ['Mr.', 'Ms.', 'Mrs.'] }
              selectedChoice={ document.ownerSalutation }
              onSelect={ choice => this.onDirectValueChange(choice, 'ownerSalutation') } 
              valid={ !cxValidateField(form.fields.ownerSalutation) }
              ref={ this.refs.ownerSalutation } />
          </div>

          <div className="hi-input-group">
            <label className="hi-input-group__label">Owner's Full Name</label>
            <div className="d-flex">
              <input type="text" placeholder="First name"
                value={ document.ownerFirstName }
                className={ cx('form-control w-50 hi-border-radius-right-0', {
                  'is-invalid': cxValidateField(form.fields.ownerFirstName)
                }) }
                onChange={ handlers(
                  this.linkToDocument('ownerFirstName'),
                  hopplerLinkState(form$, form, 'ownerFirstName')
                ) }
                ref={ this.refs.ownerFirstName } />
              <input type="text" placeholder="Last name"
                value={ document.ownerLastName }
                className={ cx('form-control w-50 hi-border-radius-left-0', {
                  'is-invalid': cxValidateField(form.fields.ownerLastName)
                }) }
                onChange={ handlers(
                  this.linkToDocument('ownerLastName'),
                  hopplerLinkState(form$, form, 'ownerLastName')
                ) }
                ref={ this.refs.ownerLastName } />
            </div>
          </div>
        </div>

        { props.documentType === InquiryDocumentType.OFFER && (
          <div className="hi-input-group">
            <label className="hi-input-group__label">Broker's Full Name</label>
            <input type="text" placeholder="Name"
              value={ document.brokerName }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.brokerName)
              }) }
              onChange={ handlers(
                this.linkToDocument('brokerName'),
                hopplerLinkState(form$, form, 'brokerName')
              ) }
              ref={ this.refs.brokerName } />
          </div>
        ) }

        { props.documentType === InquiryDocumentType.OFFER && (
          <div className="hi-docnamesform__form-row hi-docnamesform__broker-contact-group">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Broker's Email Address</label>
              <input type="email" placeholder="Email"
                value={ document.brokerEmail }
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.brokerEmail)
                }) }
                onChange={ handlers(
                  this.linkToDocument('brokerEmail'),
                  hopplerLinkState(form$, form, 'brokerEmail')
                ) }
                ref={ this.refs.brokerEmail } />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Broker's Fax Number</label>
              <input type="tel" placeholder="Fax number"
                value={ document.faxNumber }
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.faxNumber)
                }) }
                onChange={ handlers(
                  this.linkToDocument('faxNumber'),
                  hopplerLinkState(form$, form, 'faxNumber')
                ) }
                ref={ this.refs.faxNumber } />
            </div>
          </div>
        )}

        <div className="hi-docnamesform__button-row">
          <button className="btn btn-secondary hi-docnamesform__next-button"
            onClick={ () => this.onNextClick() }>
            Next
          </button>
        </div>
      </div>
    );

    // const end = performance.now();
    // console.log(`Rendered in ${end - start}ms`);
    return ui;
  }

  render(props: InqDocumentNamesFormProps, state: InqDocumentNamesFormState) {
    return (
      <div className="hi-docmodal__tabbed-container">
        <InqDocumentModalTabs activeTab={ props.activeTab }
          onTabClick={ (tabId) => this.onTabClick(tabId) } />

        <div className="hi-docmodal__tab-content" ref={ this.formScrollerRef }>
          { this.renderForm(props, state) }
        </div>
      </div>
    )
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  componentWillReceiveProps(nextProps: InqDocumentNamesFormProps) {
    const newDocument = nextProps.document;

    // When switching from "Continue without a Property" and an LOI template,
    // the data inside the form object is not immediately sync'd up.
    //
    // So we manually sync it up:
    const fields = this.createFormFields(newDocument, nextProps.documentType);
    const form = Form.cloneFrom(this.state.form); // Ensure that all other form flags are copied correctly
    form.fields = fields;
    this.form$.next(form);
  }
}
